import { Appointment, TaskListItem } from '@myapp/contract'
import { resolveBreakForDay } from '../../../entities/schedule/model/useBreakBlock'
import { isWorkingDay } from '../../../shared/lib/workingDays'
import { getHourFromIso } from '../../worker-calendar/model/calendarConstants'
import { getAbsenceCategoryLabel } from "../../../entities/absence"
import type { TechnicianScheduleView } from '../../../entities/schedule'
import { workOrderAddress } from '../../../shared/helpers/address'
import type { ConflictItem } from '../../../shared/lib/schedule'

const BREAK_DURATION_MIN = 30

/**
 * Re-exported, not defined here: the type now lives in `shared/lib/schedule` so that
 * `worker-calendar` can use it without importing sideways from this widget.
 */
export type { ConflictItem } from '../../../shared/lib/schedule'

interface DraftRange {
  plannedStart: string // work start ISO
  travelDuration: number // minutes
  workDuration: number // minutes
  departureDuration: number // minutes
}

function overlaps(startA: number, endA: number, startB: number, endB: number): boolean {
  return startA < endB && startB < endA
}

function toMs(iso: string): number {
  return new Date(iso).getTime()
}

function formatTime(ms: number): string {
  const d = new Date(ms)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function checkCollisions(
  draft: DraftRange,
  technicianData: TechnicianScheduleView | null,
  existingTasks: TaskListItem[],
): ConflictItem[] {
  const workStartMs = toMs(draft.plannedStart)
  const draftStart = workStartMs - draft.travelDuration * 60_000
  const draftEnd = workStartMs + (draft.workDuration + draft.departureDuration) * 60_000

  const conflicts: ConflictItem[] = []
  const dayIso = new Date(draft.plannedStart).toISOString().slice(0, 10)

  // --- Past-time check ---
  if (draftStart < Date.now()) {
    conflicts.push({ label: 'Zeitpunkt liegt in der Vergangenheit', timeLabel: '' })
    return conflicts
  }

  // --- Working day & shift hours ---
  if (technicianData) {
    if (!isWorkingDay(technicianData.workingDays, dayIso)) {
      conflicts.push({ label: 'Kein Arbeitstag', timeLabel: '' })
      return conflicts
    }

    // Shift hours: stored as "local time as UTC" → getUTCHours() = intended local hour
    const shiftStartHour = technicianData.shiftStart ? getHourFromIso(technicianData.shiftStart) : null
    const shiftEndHour = technicianData.shiftEnd ? getHourFromIso(technicianData.shiftEnd) : null

    // Draft times as hour-of-day (local getHours matches the "stored-as-UTC" convention)
    const draftStartHour = new Date(draftStart).getHours() + new Date(draftStart).getMinutes() / 60
    const draftEndHour = new Date(draftEnd).getHours() + new Date(draftEnd).getMinutes() / 60

    if (shiftStartHour != null && shiftEndHour != null && (draftStartHour < shiftStartHour || draftEndHour > shiftEndHour)) {
      conflicts.push({ label: 'Außerhalb der Arbeitszeit', timeLabel: '' })
    }
  }

  // --- Appointments ---
  if (technicianData) {
    const dayAppointments = technicianData.appointments.filter(
      (a) => !a.schedule.needsRescheduling && new Date(a.schedule.plannedWorkStartsAt).toISOString().slice(0, 10) === dayIso,
    )

    for (const appt of dayAppointments) {
      const isCompleted = appt.status === 'COMPLETED' || appt.status === 'COMPLETED_WITH_FOLLOW_UP'
      const hasActualData = isCompleted &&
        appt.schedule.actualWorkStartsAt != null &&
        appt.schedule.actualTravelMinutes != null &&
        appt.schedule.actualWorkMinutes != null

      const travelDur = hasActualData ? appt.schedule.actualTravelMinutes! : appt.schedule.plannedTravelMinutes
      const workDur = hasActualData ? appt.schedule.actualWorkMinutes! : appt.schedule.plannedWorkMinutes
      const departureDur = (hasActualData && appt.schedule.actualDepartureMinutes != null)
        ? appt.schedule.actualDepartureMinutes
        : appt.schedule.plannedDepartureMinutes

      // Mirror the worker calendar's block split: actualStart = початок роботи, рахуємо вперед
      const workStartMs = hasActualData ? toMs(appt.schedule.actualWorkStartsAt!) : toMs(appt.schedule.plannedWorkStartsAt)
      const apptStart = workStartMs - travelDur * 60_000
      const apptEnd = workStartMs + (workDur + departureDur) * 60_000

      if (overlaps(draftStart, draftEnd, apptStart, apptEnd)) {
        const address = workOrderAddress(appt.workOrder?.external) ?? 'Termin'
        conflicts.push({
          label: address,
          timeLabel: formatTime(workStartMs),
        })
      }
    }

    // --- TM entries ---
    const dayStart = new Date(`${dayIso}T00:00:00`).getTime()
    const dayEnd = new Date(`${dayIso}T23:59:59.999`).getTime()

    const dayTMEntries = technicianData.timeManagementEntries.filter(
      (e) => toMs(e.startsAt) <= dayEnd && toMs(e.endsAt) >= dayStart,
    )

    for (const entry of dayTMEntries) {
      const entryStart = Math.max(toMs(entry.startsAt), dayStart)
      const entryEnd = Math.min(toMs(entry.endsAt), dayEnd)

      if (overlaps(draftStart, draftEnd, entryStart, entryEnd)) {
        conflicts.push({
          label: getAbsenceCategoryLabel(entry.category),
          timeLabel: formatTime(entryStart),
        })
      }
    }

    // --- Break ---
    // The server omits the break on non-working days, so its absence is the working-day check.
    const breakBlock = resolveBreakForDay(technicianData, dayIso)
    if (breakBlock) {
      const breakStart = toMs(breakBlock.startTimeIso)
      const breakEnd = breakStart + technicianData.breakDurationMinutes * 60_000

      if (overlaps(draftStart, draftEnd, breakStart, breakEnd)) {
        conflicts.push({
          label: 'Pause',
          timeLabel: formatTime(breakStart),
        })
      }
    }
  }

  // --- Existing tasks (excluding current) ---
  for (const task of existingTasks) {
    if (!task.schedule?.plannedWorkStartsAt) continue
    if (new Date(task.schedule?.plannedWorkStartsAt).toISOString().slice(0, 10) !== dayIso) continue

    const taskWorkMs = toMs(task.schedule?.plannedWorkStartsAt)
    const taskStart = taskWorkMs - task.schedule?.plannedTravelMinutes * 60_000
    const taskEnd = taskWorkMs + (task.schedule?.plannedWorkMinutes + task.schedule?.plannedDepartureMinutes) * 60_000

    if (overlaps(draftStart, draftEnd, taskStart, taskEnd)) {
      conflicts.push({
        label: task.title,
        timeLabel: formatTime(taskWorkMs),
      })
    }
  }

  return conflicts
}
